import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { KpiCard } from '../components/KpiCard';
import { FilterBar } from '../components/FilterBar';
import FunnelChart from '../components/FunnelChart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { format, subDays, differenceInDays, parseISO } from 'date-fns';
import { Info } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, Cell, Legend
} from 'recharts';

const CHART_COLORS = ['#2563EB', '#3B82F6', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#1E40AF'];

const ChartTooltip = ({ active, payload, label, fmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {fmt === 'currency' ? '$' + Number(p.value).toLocaleString() : fmt === 'percent' ? Number(p.value).toFixed(1) + '%' : p.value}
        </p>
      ))}
    </div>
  );
};

// KPI card with info tooltip — simple custom popover approach (no context needed)
const KpiCardWithTooltip = ({ tooltipText, ...props }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <KpiCard {...props} />
      {tooltipText && (
        <div className="absolute top-2 right-8 z-10">
          <div className="cursor-help p-0.5">
            <Info size={11} className="text-slate-400" />
          </div>
          {show && (
            <div className="absolute right-0 top-6 w-64 bg-slate-900 text-white text-xs p-3 rounded-lg shadow-xl border border-white/10 z-50 leading-relaxed">
              {tooltipText}
              <div className="absolute -top-1.5 right-2 w-3 h-3 bg-slate-900 border-t border-l border-white/10 rotate-45" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const defaultGoals = {};

export default function DashboardPage() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [monthlyGoals, setMonthlyGoals] = useState({});
  const [filters, setFilters] = useState({
    date_from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd'),
    location_id: '', referral_source: '', payment_method: ''
  });

  const handleGoalChange = useCallback((key, value) => {
    // Goals are now server-side; this is a no-op (editing done on Goals page)
    // But we keep the API for KpiCard compatibility
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch {}
  }, []);

  const fetchDropdowns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dropdowns`);
      setDropdowns(res.data);
    } catch {}
  }, []);

  const fetchGoals = useCallback(async () => {
    try {
      if (filters.location_id) {
        // Specific location filter → get exactly that location's goals
        const res = await axios.get(`${API}/goals`, { params: { location_id: filters.location_id } });
        setMonthlyGoals(res.data.goals || {});
      } else {
        // No location filter → sum ALL location goals
        const res = await axios.get(`${API}/goals/all-locations`);
        const locGoals = res.data.location_goals || {};
        const COUNT_KPI_KEYS = new Set(['fa_presented','fa_sold_total','fa_sold_same_day','fa_sold_previous',
          'arches_total','arches_same_day','arches_previous','consults_scheduled','consults_showed',
          'inbound_calls','total_revenue','collections_eod','surgical_production','total_collected']);
        const summed = {};
        const rateSums = {};
        const rateCounts = {};
        Object.values(locGoals).forEach(({ goals }) => {
          Object.entries(goals || {}).forEach(([key, val]) => {
            if (val === null || val === undefined) return;
            const n = Number(val);
            if (COUNT_KPI_KEYS.has(key)) {
              summed[key] = (summed[key] || 0) + n;
            } else {
              // Rate KPIs: average across locations that have this goal set
              rateSums[key] = (rateSums[key] || 0) + n;
              rateCounts[key] = (rateCounts[key] || 0) + 1;
            }
          });
        });
        Object.keys(rateSums).forEach(key => {
          if (rateCounts[key] > 0) summed[key] = Math.round(rateSums[key] / rateCounts[key] * 10) / 10;
        });
        setMonthlyGoals(summed);
      }
    } catch {}
  }, [filters.location_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.referral_source) params.referral_source = filters.referral_source;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      const res = await axios.get(`${API}/dashboard/kpis`, { params });
      setKpis(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchLocations(); fetchDropdowns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchKpis(); }, [fetchKpis]);
  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const kpiProps = (key) => ({
    goalKey: key,
    monthlyGoals,
    onGoalChange: handleGoalChange,
    periodDays,
    periodLabel,
    locationCount: locations.filter(l => l.active).length,
  });

  // Compute period days from filters
  const periodDays = React.useMemo(() => {
    try {
      if (!filters.date_from || !filters.date_to) return 30;
      const from = parseISO(filters.date_from);
      const to = parseISO(filters.date_to);
      return Math.max(1, differenceInDays(to, from) + 1);
    } catch { return 30; }
  }, [filters.date_from, filters.date_to]);

  const periodLabel = `${periodDays} days`;

  // MTD progress calculation
  const mtdProgress = React.useMemo(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const dayOfMonth = now.getDate();
    const pct = Math.round(dayOfMonth / daysInMonth * 100);
    return { dayOfMonth, daysInMonth, pct };
  }, []);

  // Add close rate trend data to daily trend
  const trendWithRates = (kpis?.daily_trend || []).map(d => ({
    ...d,
    close_rate: d.fa_presented > 0 ? Math.round(d.fa_sold / d.fa_presented * 100 * 10) / 10 : 0,
    show_rate_calc: 0 // can't calc without scheduled per day easily, omit
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight">Performance Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            Full Arch ROI &bull; {filters.date_from} to {filters.date_to}
            {periodDays ? <span className="ml-1 text-slate-500">({periodDays} days)</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {Object.keys(monthlyGoals).length > 0 ? (
            <span className="text-xs px-2 py-1 rounded-lg font-medium badge-blue">
              🎯 {Object.keys(monthlyGoals).length} goals active · click ◎ on any card
            </span>
          ) : (
            <a href="/goals" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium">
              🎯 Set monthly goals →
            </a>
          )}
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        locations={locations.filter(l => l.active)}
        referralSources={dropdowns.referral_sources}
        paymentMethods={dropdowns.payment_methods}
      />

      {/* ── GOAL TRACKER BAR (Arches as primary goal) ── */}
      <div className="surface-card px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Primary: Arches Goal */}
          {!loading && kpis && monthlyGoals['arches_total'] ? (() => {
            const monthlyGoal = monthlyGoals['arches_total'];
            const prorated = Math.round(monthlyGoal * periodDays / 30.44 * 10) / 10;
            const actual = kpis?.full_arch?.arches_total || 0;
            const pct = prorated > 0 ? Math.round(actual / prorated * 100) : 0;
            const meets = actual >= prorated;
            return (
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: meets ? '#16A34A' : '#1D4ED8' }} />
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Arches Goal</span>
                    <span className="text-xs text-slate-400">monthly target</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500">{actual} <span className="text-slate-300">/</span> {prorated} pro-rated ({periodDays}d)</span>
                    <span className={`font-bold text-sm tabular-nums ${meets ? 'text-green-700' : 'text-blue-700'}`}>{pct}%</span>
                  </div>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(100, pct)}%`, background: meets ? 'linear-gradient(90deg,#15803D,#22C55E)' : 'linear-gradient(90deg,#1D4ED8,#3B82F6)' }} />
                </div>
              </div>
            );
          })() : (
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="section-label">Monthly Progress</span>
                <span className="text-xs text-slate-500">
                  Day {mtdProgress.dayOfMonth} of {mtdProgress.daysInMonth}
                </span>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${mtdProgress.pct}%`, background: 'linear-gradient(90deg,#1D4ED8,#3B82F6)' }} />
              </div>
              {Object.keys(monthlyGoals).length === 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  Set goals in <a href="/goals" className="text-blue-600 hover:underline">Monthly Goals</a> to track progress here
                </p>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="hidden sm:block w-px h-10 bg-slate-200" />

          {/* Secondary: Calendar day */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Month</p>
              <p className="text-lg font-bold text-slate-900 tabular-nums font-heading">{mtdProgress.pct}%</p>
              <p className="text-xs text-slate-400">Day {mtdProgress.dayOfMonth}/{mtdProgress.daysInMonth}</p>
            </div>
            {Object.keys(monthlyGoals).length > 0 && (
              <>
                <div className="w-px h-8 bg-slate-200" />
                <div className="text-center">
                  <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Goals</p>
                  <p className="text-lg font-bold text-blue-700 tabular-nums font-heading">{Object.keys(monthlyGoals).length}</p>
                  <p className="text-xs text-slate-400">active</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── COLLECTIONS GOAL CARD (Catalina-specific KPI tile) ── */}
      {!loading && kpis && (() => {
        const goal = Number(monthlyGoals?.collections_eod || monthlyGoals?.total_collected || 0);
        const actual = Number(kpis?.sales?.collected || kpis?.collections?.total || kpis?.sales?.total_collected || 0);
        const prorated = goal > 0 ? Math.round(goal * periodDays / 30.44) : 0;
        const pct = goal > 0 ? Math.round(actual / goal * 100) : 0;
        const remaining = Math.max(goal - actual, 0);
        const onPace = prorated > 0 && actual >= prorated;
        if (goal === 0) return null;
        return (
          <div className="surface-card px-5 py-4" data-testid="collections-goal-card">
            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="flex-shrink-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">Collections Goal · This Month</p>
                <p className="font-heading text-3xl font-medium mt-1" style={{ color: '#0E0E0E' }}>${goal.toLocaleString()}</p>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs text-stone-500">
                    Collected <span className="font-semibold" style={{ color: '#0E0E0E' }}>${actual.toLocaleString()}</span>
                    <span className="text-stone-400 mx-1">·</span>
                    pace <span className={onPace ? 'text-emerald-700' : 'text-rose-700'}>${prorated.toLocaleString()}</span>
                  </span>
                  <span className="font-heading text-xl tabular-nums" style={{ color: onPace ? '#15803D' : '#C9A961' }}>{pct}%</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(201,169,97,0.12)' }}>
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(pct, 100)}%`, background: onPace ? 'linear-gradient(90deg,#15803D,#22C55E)' : 'linear-gradient(90deg,#8A6A2A,#C9A961)' }} />
                </div>
                {remaining > 0 ? (
                  <p className="text-[11px] text-stone-500 mt-1.5">
                    <span className="font-semibold" style={{ color: '#9F1239' }}>${remaining.toLocaleString()}</span> to hit goal
                  </p>
                ) : (
                  <p className="text-[11px] mt-1.5 font-semibold" style={{ color: '#15803D' }}>🎉 Goal reached</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── CONSULT FUNNEL (top — visual + KPI cards) ── */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Consult Funnel</h2>
          <div className="flex-1 h-px bg-slate-100" />
        </div>

        {/* Visual Funnel */}
        {!loading && kpis && (
          <div className="surface-card mb-4 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Conversion Funnel</h3>
              <span className="text-xs text-slate-400">{filters.date_from} → {filters.date_to}</span>
            </div>
            <FunnelChart
              data={[
                { label: 'Leads',    value: kpis?.consults?.inbound_calls,         goalKey: 'inbound_calls' },
                { label: 'Bookings', value: kpis?.consults?.appointments_scheduled, goalKey: 'consults_scheduled' },
                { label: 'Shows',    value: kpis?.consults?.showed,                 goalKey: 'consults_showed' },
                { label: 'Cases',    value: kpis?.full_arch?.sold_total,            goalKey: 'fa_sold_total' },
                { label: 'Arches',   value: kpis?.full_arch?.arches_total,          goalKey: 'arches_total' },
              ].filter(d => (d.value || 0) > 0)}
              monthlyGoals={monthlyGoals}
              periodDays={periodDays}
            />
          </div>
        )}
        {loading && <Skeleton className="h-72 rounded-xl mb-4 bg-slate-100" />}

        {/* Funnel KPI Cards */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-28 bg-slate-100 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Leads" value={kpis?.consults?.inbound_calls}
              subValue={kpis?.consults?.calls_answered} subLabel="answered" index={0}
              {...kpiProps('inbound_calls')} />
            <KpiCard label="Bookings" value={kpis?.consults?.appointments_scheduled} index={1}
              {...kpiProps('consults_scheduled')} />
            <KpiCard label="Consults Scheduled" value={kpis?.consults?.scheduled} index={2}
              {...kpiProps('consults_scheduled')} />
            <KpiCard label="Consults Showed" value={kpis?.consults?.showed} index={3}
              {...kpiProps('consults_showed')} />
            <KpiCard label="FA Presented" value={kpis?.full_arch?.presented} primary index={4}
              {...kpiProps('fa_presented')} />
            <KpiCard label="FA Sold (Total)" value={kpis?.full_arch?.sold_total} primary
              subValue={kpis?.full_arch?.sold_same_day} subLabel="same day" index={5}
              {...kpiProps('fa_sold_total')} />
            <KpiCard label="Arches Sold" value={kpis?.full_arch?.arches_total} primary
              subValue={kpis?.full_arch?.arches_same_day} subLabel="same day" index={6}
              {...kpiProps('arches_total')} />
          </div>
        )}

        {/* Rate KPI goals row */}
        {!loading && kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
            <KpiCardWithTooltip label="Booking Rate" index={0} format="percent"
              value={kpis?.consults?.appointments_scheduled > 0 && kpis?.consults?.inbound_calls > 0
                ? Math.round(kpis.consults.appointments_scheduled / kpis.consults.inbound_calls * 1000) / 10
                : null}
              {...kpiProps('show_rate')}
              tooltipText="Leads who booked an appointment. Formula: Bookings ÷ Inbound Calls × 100%" />
            <KpiCardWithTooltip label="Show Rate" index={1} format="percent"
              value={kpis?.consults?.show_rate}
              {...kpiProps('show_rate')}
              tooltipText="Booked appointments that showed. Formula: Consults Showed ÷ Consults Scheduled × 100%" />
            <KpiCardWithTooltip label="FA Conversion Rate" index={2} format="percent"
              value={kpis?.consults?.showed > 0 && kpis?.full_arch?.presented > 0
                ? Math.round(kpis.full_arch.presented / kpis.consults.showed * 1000) / 10
                : null}
              {...kpiProps('show_rate')}
              tooltipText="Shows that became Full Arch presentations. Formula: FA Presented ÷ Consults Showed × 100%" />
            <KpiCardWithTooltip label="Same Day Close %" index={3} format="percent"
              value={kpis?.full_arch?.same_day_close_rate}
              {...kpiProps('same_day_close_rate')}
              tooltipText="FA cases closed the same day they were presented. Formula: FA Sold Same Day ÷ FA Presented × 100%" />
            <KpiCardWithTooltip label="Overall Close %" index={4} format="percent"
              value={kpis?.full_arch?.normalized_close_rate}
              {...kpiProps('normalized_close_rate')}
              tooltipText="All FA cases closed (same day + from previous consults) ÷ FA Presented × 100%. Your main blended close rate." />
          </div>
        )}

        {/* Qualified Consults */}
        {!loading && kpis?.consults?.qualified_consults > 0 && (
          <div className="mt-3 max-w-xs">
            <KpiCardWithTooltip label="Qualified Consults" value={kpis.consults.qualified_consults} index={0}
              tooltipText="Patients who showed AND were clinically and financially viable candidates for Full Arch treatment."
              {...kpiProps('consults_showed')} />
          </div>
        )}
      </div>

      {/* ── FULL ARCH PRIORITY SECTION ── */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Full Arch Close Rates</h2>
          <div className="flex-1 h-px bg-slate-100" />
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 bg-slate-100 rounded-xl" />)}
          </div>
        ) : (
          <>
            {/* Close Rate Cards with Tooltips */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
              <KpiCardWithTooltip
                label="Same Day Close Rate" value={kpis?.full_arch?.same_day_close_rate}
                format="percent" primary index={0}
                status={!monthlyGoals['same_day_close_rate'] ? (kpis?.full_arch?.same_day_close_rate >= 25 ? 'good' : kpis?.full_arch?.same_day_close_rate >= 15 ? 'avg' : 'bad') : undefined}
                {...kpiProps('same_day_close_rate')}
                tooltipText="FA Cases Sold From Today's Consults ÷ FA Cases Presented. Measures how many presented cases close on the same day they're seen."
              />
              <KpiCardWithTooltip
                label="Normalized Close Rate" value={kpis?.full_arch?.normalized_close_rate}
                format="percent" primary index={1}
                subValue={kpis?.full_arch?.sold_previous} subLabel="from prev"
                status={!monthlyGoals['normalized_close_rate'] ? (kpis?.full_arch?.normalized_close_rate >= 40 ? 'good' : kpis?.full_arch?.normalized_close_rate >= 25 ? 'avg' : 'bad') : undefined}
                {...kpiProps('normalized_close_rate')}
                tooltipText="(FA Sold Same Day + FA Sold From Previous) ÷ FA Presented. The main blended operational close rate for the selected period."
              />
              <KpiCardWithTooltip
                label="30-Day Close Rate" value={kpis?.full_arch?.close_rate_30d}
                format="percent" primary index={2}
                tooltipText="Normalized close rate calculated only from the last 30 days (always a fixed 30-day window regardless of your date filter). Best for tracking recent operational trends."
                {...kpiProps('same_day_close_rate')}
              />
              <KpiCardWithTooltip
                label="90-Day Close Rate" value={kpis?.full_arch?.close_rate_90d}
                format="percent" primary index={3}
                tooltipText="Normalized close rate calculated from the last 90 days (fixed window). This is your 90-day rolling average — smooths out short-term volatility."
                {...kpiProps('normalized_close_rate')}
              />
            </div>
            {/* Other Full Arch KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <KpiCard label="FA Sold (Same Day)" value={kpis?.full_arch?.sold_same_day} primary index={0}
                {...kpiProps('fa_sold_same_day')} />
              <KpiCard label="FA Sold (Previous)" value={kpis?.full_arch?.sold_previous} primary index={1}
                {...kpiProps('fa_sold_previous')} />
              <KpiCard label="Arches (Same Day)" value={kpis?.full_arch?.arches_same_day} primary index={2}
                {...kpiProps('arches_same_day')} />
              <KpiCard label="Arches (From Previous)" value={kpis?.full_arch?.arches_previous} primary index={3}
                {...kpiProps('arches_previous')} />
            </div>
          </>
        )}
      </div>

      {/* ── REVENUE ── */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Revenue</h2>
          <div className="flex-1 h-px bg-slate-100" />
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 bg-slate-100 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label="Total Revenue (Sales)" value={kpis?.revenue?.total} format="currency" primary index={0}
              {...kpiProps('total_revenue')} />
            <KpiCardWithTooltip
              label="Total Collected (Daily)" value={kpis?.revenue?.total_collected} format="currency" index={1}
              {...kpiProps('total_collected')}
              tooltipText="Total money collected at the office each day, entered via the EOD Summary. Includes payments on new sales, previous sales, deposits, and all other daily collections."
            />
            <KpiCardWithTooltip
              label="Collected on Sales" value={kpis?.revenue?.collected_on_sales} format="currency" index={2}
              tooltipText="Sum of the 'collected today' field from individual Sale Records. This is the amount collected at the time each sale was recorded — not total daily collections."
            />
            <KpiCard label="Surgical Production" value={kpis?.revenue?.surgical_production} format="currency" index={3}
              {...kpiProps('surgical_production')} />
          </div>
        )}
      </div>

      {/* ── SPEED METRICS ── */}
      {!loading && kpis?.speed && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 rounded-full bg-purple-600" />
            <h2 className="font-heading font-semibold text-slate-600 text-xs uppercase tracking-widest">Speed Metrics</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-sm">
            <KpiCard label="Avg Days Consult \u2192 Close" value={kpis.speed.avg_consult_to_close_days} format="days" index={0}
              {...kpiProps('consult_to_close_days')} />
            <KpiCard label="Avg Days Close \u2192 Treatment" value={kpis.speed.avg_close_to_treatment_days} format="days" index={1}
              {...kpiProps('close_to_treatment_days')} />
          </div>
        </div>
      )}

      {/* ── CHARTS ── */}
      {!loading && kpis && trendWithRates.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* FA Trend Chart */}
          <div className="xl:col-span-2">
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">
                  Daily Trend — FA Presented vs Sold
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240} data-testid="dashboard-chart-roi-trend">
                  <LineChart data={trendWithRates} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={d => d.slice(5)} interval={Math.max(0, Math.floor(trendWithRates.length / 8))} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <RechartTooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="fa_presented" stroke="#3B82F6" name="FA Presented" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="fa_sold" stroke="#2563EB" name="FA Sold" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="consults" stroke="#94A3B8" name="Consults Showed" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Close Rate Trend */}
          <div>
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">
                  Close Rate Trend (%)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trendWithRates} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={d => d.slice(5)} interval={Math.max(0, Math.floor(trendWithRates.length / 6))} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                    <RechartTooltip content={<ChartTooltip fmt="percent" />} />
                    {monthlyGoals['normalized_close_rate'] && (
                      <Line type="monotone" dataKey={() => Number(monthlyGoals['normalized_close_rate'])} stroke="#16A34A"
                        strokeDasharray="6 3" name="Goal" strokeWidth={1.5} dot={false} />
                    )}
                    <Line type="monotone" dataKey="close_rate" stroke="#2563EB" name="Close Rate %" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Collections + Revenue breakdown row */}
      {!loading && kpis && trendWithRates.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Daily Collections */}
          <div className="xl:col-span-2">
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Daily Collections ($)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendWithRates} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={d => d.slice(5)} interval={Math.max(0, Math.floor(trendWithRates.length / 8))} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'k'} />
                    <RechartTooltip content={<ChartTooltip fmt="currency" />} />
                    <Bar dataKey="collections" fill="#2563EB" name="Collections" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Referral */}
          {kpis.revenue?.by_referral?.length > 0 && (
            <div>
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Revenue by Referral</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart layout="vertical" data={kpis.revenue.by_referral.slice(0, 6)}
                      margin={{ top: 5, right: 30, left: 85, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'k'} />
                      <YAxis type="category" dataKey="source" tick={{ fontSize: 10, fill: '#64748b' }} width={85} />
                      <RechartTooltip content={<ChartTooltip fmt="currency" />} />
                      <Bar dataKey="amount" name="Revenue" radius={[0, 2, 2, 0]}>
                        {kpis.revenue.by_referral.slice(0, 6).map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Revenue by Payment + Consult Volume */}
      {!loading && kpis && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {kpis.revenue?.by_payment?.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Revenue by Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart layout="vertical" data={kpis.revenue.by_payment}
                    margin={{ top: 5, right: 30, left: 90, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'k'} />
                    <YAxis type="category" dataKey="method" tick={{ fontSize: 10, fill: '#64748b' }} width={90} />
                    <RechartTooltip content={<ChartTooltip fmt="currency" />} />
                    <Bar dataKey="amount" name="Revenue" radius={[0, 2, 2, 0]}>
                      {kpis.revenue.by_payment.map((entry, i) => (
                        <Cell key={entry.method || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Consult volume trend */}
          {trendWithRates.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">FA Sold vs Arches Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendWithRates.slice(-30)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={d => d.slice(5)} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <RechartTooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="fa_sold" fill="#2563EB" name="FA Sold" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="fa_presented" fill="#E2E8F0" name="FA Presented" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && kpis && trendWithRates.length === 0 && (
        <Card className="bg-white shadow-sm">
          <CardContent className="py-16 text-center">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-slate-700 font-semibold">No data for selected period</p>
            <p className="text-slate-500 text-sm mt-1">Submit EOD records or load the demo to see charts</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
